import React, { useState, useEffect, useRef, useCallback } from "react";
import { projectApi, taskDetailApi } from "../../backend/client";
import { useCurrentUser } from "../../backend/queries/auth";

import { Link, useNavigate, useParams, useLocation, useSearchParams } from "react-router-dom";

import { CheckCircle2, Circle, X } from "lucide-react";
import TaskInfoBox from "./taskInfoBox";
import { useProjectTasks, taskLabel } from "../../hooks/useProjectTasks";
import {
  ProjectStatusBadge,
  ProjectTypeBadge,
} from "../../components/domain-badges";
import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";

const Home = () => {
  const { data: currentUser } = useCurrentUser();
  const user = currentUser?.profile || currentUser || null;
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState(null);

  // Agent + project details (also used in "Project Information" card)
  const [agentDetails, setAgentDetails] = useState({
    name: "",
    email: "",
    contactnumber: "",
    project_start_date: "",
    project_end_date: "",
    relocating_from: "",
    relocating_to: "",
    project_type: "",
    project_notes: "",
    status: "",
  });
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { projectId: routeProjectId } = useParams();
  const isAgentPeek = location.state?.fromAgent === true;
  const forcedProjectId = location.state?.projectId
    ? Number(location.state.projectId)
    : null;
  // Set from the "View" button on My Projects (?projectId=123). Only ever
  // used to pick a starting selection out of this client's own project list
  // below — never fetched directly, so a stale or foreign id can't leak
  // another client's project.
  const requestedProjectId = routeProjectId ? Number(routeProjectId) : searchParams.get("projectId")
    ? Number(searchParams.get("projectId"))
    : null;

  const [taskProperties, setTaskProperties] = useState([]);

  // 1 = relocation, 2 = downsizing
  const [selectedClientTypeId, setSelectedClientTypeId] = useState(null);

  // Toasts when step changes
  const [toasts, setToasts] = useState([]);
  const stepRef = useRef(null);

  // airport pickup details for client display
  const [airportDetails, setAirportDetails] = useState(null);

  // insurance details for client display
  const [insurances, setInsurances] = useState([]);

  // Property viewing for client display
  const [propertyViewings, setPropertyViewings] = useState([]);
  //cleaner + rubbish booking details for client display
  const [cleanerBooking, setCleanerBooking] = useState(null);
  const [rubbishBooking, setRubbishBooking] = useState(null);

  const [boxDelivery, setBoxDelivery] = useState(null);

  const [hotelTransfer, setHotelTransfer] = useState(null);

  const [elevatorAccess, setElevatorAccess] = useState(null);
  const [declutterBooking, setDeclutterBooking] = useState(null);

  const pushToast = useCallback((stepNum, stepTitle) => {
    const id = Date.now() + Math.random();
    setToasts((ts) => [
      ...ts,
      {
        id,
        title: "Progress updated",
        body: `Now on step ${stepNum}: ${stepTitle}`,
      },
    ]);
    setTimeout(() => {
      setToasts((cur) => cur.filter((x) => x.id !== id));
    }, 4500);
  }, []);

  //  helpers
  function formatDate(dateString) {
    if (!dateString) return "N/A";
    const d = new Date(dateString);
    const dd = String(d.getUTCDate()).padStart(2, "0");
    const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
    const yy = d.getUTCFullYear();
    return `${dd}/${mm}/${yy}`;
  }

  // ---- data loads ----

  // Load projects for this user
  useEffect(() => {
    (async () => {
      if (isAgentPeek && forcedProjectId) {
        // Agent-peek: fetch the specific project by id (do NOT filter by customer_id)
        const { data, error } = await projectApi.get(forcedProjectId);

        if (error || !data) {
          console.error("Agent-peek: project not found", error);
          setProjects([]);
          setSelectedProjectId(null);
          return;
        }
        setProjects([data]);
        setSelectedProjectId(Number(data.id));
        return;
      }

      // 👤 Normal client path (uses logged-in client's id)
      if (!user?.id) return;
      const { data, error } = await projectApi.list();

      if (error) {
        console.error("Error fetching projects:", error);
        return;
      }
      const sortedProjects = (data || []).sort(
        (left, right) =>
          new Date(right.project_start_date || 0).getTime() -
          new Date(left.project_start_date || 0).getTime(),
      );
      setProjects(sortedProjects);
      const requested =
        requestedProjectId != null &&
        sortedProjects.some(
          (project) => Number(project.id) === requestedProjectId,
        )
          ? requestedProjectId
          : null;
      setSelectedProjectId(
        requestedProjectId != null ? requested : (sortedProjects.length ? Number(sortedProjects[0].id) : null),
      );
    })();
  }, [isAgentPeek, forcedProjectId, user?.id, requestedProjectId]);

  // Task list + progress/step math come from the shared hook (the same one
  // Project Details uses) instead of being computed locally here.
  const {
    tasks: allTasks,
    visibleTasks,
    totalTasks,
    upcomingList,
    completedTasks,
    percentageComplete,
    currentStep,
    currentTaskLabel,
    loading: tasksLoading,
    refetch: refetchTasks,
  } = useProjectTasks(selectedProjectId);

  // Fetch the extra per-task detail records the Task Information card needs.
  // These aren't part of the shared task list/progress hook above.
  const fetchTaskDetails = useCallback(
    async (projectId) => {
      const { data: propsData, error: propsErr } = await taskDetailApi.list(
        "properties",
        {
          project_id: Number(projectId),
          order: "created_at",
          ascending: true,
        },
      );
      if (!propsErr) setTaskProperties(propsData || []);

      //  fetch airport pickup details for client display
      const { data: air, error: airErr } = await taskDetailApi.list(
        "flight-arrivals",
        {
          project_id: Number(projectId),
          limit: 1,
        },
      );
      if (!airErr) setAirportDetails(air?.[0] || null);
      else setAirportDetails(null);

      const { data: ins, error: insErr } = await taskDetailApi.list(
        "insurance",
        {
          project_id: Number(projectId),
        },
      );
      if (!insErr) setInsurances(ins || []);
      else setInsurances([]);

      // fetch property viewings for client display
      const { data: propViewData, error: propViewErr } =
        await taskDetailApi.list("property-viewings", {
          project_id: Number(projectId),
          order: "inspection_date",
          ascending: true,
        });

      if (!propViewErr) setPropertyViewings(propViewData || []);
      else setPropertyViewings([]);
      const { data: cleaner, error: cleanerErr } = await taskDetailApi.list(
        "book-cleaner",
        {
          project_id: Number(projectId),
          limit: 1,
        },
      );

      if (!cleanerErr && cleaner && cleaner[0]) {
        setCleanerBooking(cleaner[0]);
      } else {
        setCleanerBooking(null);
      }
      const { data: rubbish, error: rubbishErr } = await taskDetailApi.list(
        "rubbish-bookings",
        {
          project_id: Number(projectId),
          order: "updated_at",
          ascending: false,
          limit: 1,
        },
      );

      if (!rubbishErr && rubbish && rubbish[0]) {
        setRubbishBooking(rubbish[0]);
      } else {
        setRubbishBooking(null);
      }

      // fetch hotel transfer details for client display
      const { data: ht, error: htErr } = await taskDetailApi.list(
        "hotel-transfers",
        {
          project_id: Number(projectId),
          limit: 1,
        },
      );

      if (!htErr && ht && ht[0]) {
        setHotelTransfer(ht[0]);
      } else {
        setHotelTransfer(null);
      }

      // Elevator access
      {
        const { data: elev, error: elevErr } = await taskDetailApi.list(
          "elevator-access",
          {
            project_id: Number(projectId),
            order: "id",
            ascending: false,
            limit: 1,
          },
        );

        setElevatorAccess(elevErr ? null : (elev?.[0] ?? null));
      }

      // Decluttering booking
      {
        const { data: declut, error: declutErr } = await taskDetailApi.list(
          "book-declutter",
          {
            project_id: Number(projectId),
            limit: 1,
          },
        );

        setDeclutterBooking(declutErr ? null : (declut?.[0] ?? null));
      }
      const { data: bd } = await taskDetailApi.list("box-deliveries", {
        project_id: Number(projectId),
        order: "updated_at",
        ascending: false,
        limit: 1,
      });

      setBoxDelivery(bd?.[0] ?? null);
    },
    [],
  );

  // When a project is selected, load its details + task detail records
  useEffect(() => {
    if (selectedProjectId == null) return;

    // reset UI first
    setAgentDetails({
      name: "",
      email: "",
      contactnumber: "",
      project_start_date: "",
      project_end_date: "",
      relocating_from: "",
      relocating_to: "",
      project_type: "",
      project_notes: "",
      status: "",
    });
    setTaskProperties([]);
    setSelectedClientTypeId(null);
    setAirportDetails(null);
    setCleanerBooking(null);
    setRubbishBooking(null);
    stepRef.current = null; // reset toast baseline for the new project

    // Local project details
    const p = projects.find(
      (pr) => Number(pr.id) === Number(selectedProjectId),
    );
    if (p) {
      setSelectedClientTypeId(p.client_type_id ?? null);
      setAgentDetails({
        name: p.agent ? `${p.agent.firstname} ${p.agent.lastname}` : "",
        email: p.agent?.email || "",
        contactnumber: p.agent?.contactnumber || "",
        project_start_date: formatDate(p.project_start_date),
        project_end_date: formatDate(p.project_end_date),
        relocating_from: p.relocating_from,
        relocating_to: p.relocating_to,
        project_type: p.project_type,
        project_notes: p.project_notes,
        status: p.status,
      });
    }

    fetchTaskDetails(selectedProjectId);
  }, [selectedProjectId, projects, fetchTaskDetails]);

  // Poll for task + task-detail updates
  useEffect(() => {
    if (!selectedProjectId) return;
    const id = setInterval(() => {
      refetchTasks();
      fetchTaskDetails(selectedProjectId);
    }, 8000);
    return () => clearInterval(id);
  }, [selectedProjectId, refetchTasks, fetchTaskDetails]);

  // Toast when the current step changes after the initial load for this project
  useEffect(() => {
    if (tasksLoading) return;
    if (stepRef.current === null) {
      stepRef.current = currentStep;
      return;
    }
    if (currentStep !== stepRef.current) {
      pushToast(currentStep, currentTaskLabel);
      stepRef.current = currentStep;
    }
  }, [tasksLoading, currentStep, currentTaskLabel, pushToast]);

  // derived UI state
  const selectedProject = projects.find(
    (project) => Number(project.id) === Number(selectedProjectId),
  );

  return (
    <div className="flex flex-col">
      <div className="flex flex-wrap gap-3 px-6 pt-5 text-sm">
        <Link className="underline underline-offset-4" to="/home">Overview</Link>
        <Link className="underline underline-offset-4" to={`/properties?projectId=${selectedProjectId || ''}`}>Properties & viewings</Link>
        <Link className="underline underline-offset-4" to={`/project-updates?projectId=${selectedProjectId || ''}`}>Project updates</Link>
      </div>

      <div className="flex-1 px-4 py-6 sm:px-6 lg:px-8">
        <div className="mx-auto flex max-w-7xl flex-col gap-5">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <h1 className="text-2xl font-semibold">My project</h1>
              <p className="text-sm text-muted-foreground">
                Review your project, progress, and next visible task.
              </p>
            </div>
            {projects.length > 0 && (
              <Select
                value={
                  selectedProjectId == null
                    ? undefined
                    : String(selectedProjectId)
                }
                onValueChange={(value) => { setSelectedProjectId(Number(value)); navigate(`/my-projects/${value}`); }}
              >
                <SelectTrigger className="w-full lg:w-[440px]">
                  <SelectValue placeholder="Select project" />
                </SelectTrigger>
                <SelectContent>
                  {projects.map((project) => (
                    <SelectItem key={project.id} value={String(project.id)}>
                      {project.project_type} from {project.relocating_from} to{" "}
                      {project.relocating_to}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {projects.length === 0 || selectedProjectId == null ? (
            <Card>
              <CardContent className="p-8 text-center text-sm text-muted-foreground">
                Project unavailable. Choose a project you have access to.
              </CardContent>
            </Card>
          ) : (
            <div className="flex flex-col gap-5">
              <Card>
                <CardHeader className="p-4 sm:p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <CardTitle>Project progress</CardTitle>
                      <CardDescription>
                        Step {currentStep} of {totalTasks || 1}
                      </CardDescription>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {percentageComplete}% · {completedTasks}/
                      {totalTasks || 1}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-4 pt-0 sm:p-5 sm:pt-0">
                  <div className="h-3 overflow-hidden rounded-full bg-muted">
                    <div
                      className="h-full rounded-full bg-primary transition-all duration-500"
                      style={{ width: `${percentageComplete}%` }}
                    />
                  </div>
                  <p className="mt-4 text-sm">
                    <span className="text-muted-foreground">
                      Current task:{" "}
                    </span>
                    <span className="font-medium">{currentTaskLabel}</span>
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="p-4 sm:p-5">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                      <CardTitle>Project information</CardTitle>
                      <CardDescription>
                        {selectedProject
                          ? `Project #${selectedProject.id}`
                          : "Selected project summary"}
                      </CardDescription>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <ProjectStatusBadge status={agentDetails.status} />
                      <ProjectTypeBadge type={agentDetails.project_type} />
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="grid gap-3 p-4 pt-0 sm:grid-cols-2 sm:p-5 sm:pt-0">
                  <InfoItem
                    label="Relocating from"
                    value={agentDetails.relocating_from || "N/A"}
                  />
                  <InfoItem
                    label="Relocating to"
                    value={agentDetails.relocating_to || "N/A"}
                  />
                  <InfoItem
                    label="Project start date"
                    value={agentDetails.project_start_date || "N/A"}
                  />
                  <InfoItem
                    label="Project end date"
                    value={agentDetails.project_end_date || "N/A"}
                  />
                  <InfoItem
                    label="Your agent"
                    value={agentDetails.name || "TBA"}
                  />
                  <InfoItem
                    label="Agent email"
                    value={
                      agentDetails.email ? (
                        <a
                          className="break-all text-primary underline-offset-4 hover:underline"
                          href={`mailto:${agentDetails.email}`}
                        >
                          {agentDetails.email}
                        </a>
                      ) : (
                        "N/A"
                      )
                    }
                  />
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="p-4 sm:p-5">
                  <CardTitle>Task checklist</CardTitle>
                  <CardDescription>
                    Tasks visible to you for this project, in order.
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-4 pt-0 sm:p-5 sm:pt-0">
                  {visibleTasks.length ? (
                    <ol className="space-y-2 text-sm">
                      {visibleTasks.map((t, index) => (
                        <li
                          key={t.id ?? index}
                          className="flex items-center gap-2 p-2"
                        >
                          {t.is_complete ? (
                            <CheckCircle2 className="h-4 w-4 shrink-0 text-tone-success-foreground" />
                          ) : (
                            <Circle className="h-4 w-4 shrink-0 text-muted-foreground" />
                          )}
                          <span>{taskLabel(t)}</span>
                        </li>
                      ))}
                    </ol>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      No tasks to show yet.
                    </p>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="p-4 sm:p-5">
                  <CardTitle>Task information</CardTitle>
                  <CardDescription>
                    Extra details for your current task.
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-4 pt-0 sm:p-5 sm:pt-0">
                  <TaskInfoBox
                    currentTask={upcomingList[0] ?? null}
                    projectType={agentDetails.project_type}
                    allTasks={allTasks}
                    currentStep={currentStep}
                    clientTypeId={selectedClientTypeId}
                    agentDetails={{
                      name: agentDetails.name,
                      email: agentDetails.email,
                      contactnumber: agentDetails.contactnumber,
                    }}
                    properties={taskProperties}
                    airportDetails={airportDetails}
                    insurances={insurances}
                    viewings={propertyViewings}
                    cleanerBooking={cleanerBooking}
                    rubbishBooking={rubbishBooking}
                    hotelTransfer={hotelTransfer}
                    elevatorAccess={elevatorAccess}
                    declutterBooking={declutterBooking}
                    boxDelivery={boxDelivery}
                  />
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </div>

      <div
        className="fixed right-4 top-4 z-[9999] w-[calc(100%-2rem)] max-w-md space-y-2"
        aria-live="polite"
        aria-atomic="true"
      >
        {toasts.map((t) => (
          <div
            key={t.id}
            className="rounded-md border bg-card p-4 text-card-foreground shadow-lg"
            role="alert"
            aria-live="assertive"
            aria-atomic="true"
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="font-semibold">{t.title}</div>
                <div className="mt-1 text-sm text-muted-foreground">
                  {t.body}
                </div>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="-mr-2 -mt-2 h-8 w-8"
                aria-label="Close"
                onClick={() =>
                  setToasts((prev) => prev.filter((x) => x.id !== t.id))
                }
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const InfoItem = ({ label, value }) => (
  <div className="rounded-md border bg-card p-3">
    <div className="text-xs font-medium text-muted-foreground">{label}</div>
    <div className="mt-1 break-words text-sm font-medium">{value}</div>
  </div>
);

export default Home;
